import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import type { InjuryConstraint, Sex, UserProfile } from "@jilian/shared";
import { useSettings } from "../db/hooks";
import { saveInjury, saveProfile, DEFAULT_PROFILE, DEFAULT_INJURY } from "../db/repo";
import { Button, Card, RiskTag, SectionTitle, Segmented, Sheet, Toggle } from "../components/ui";

export function Profile() {
  const nav = useNavigate();
  const [showEditProfile, setShowEditProfile] = useState(false);
  const [showEditInjury, setShowEditInjury] = useState(false);

  const settings = useSettings();
  const profile = settings?.profile ?? DEFAULT_PROFILE;
  const injury = settings?.injury ?? DEFAULT_INJURY;

  // 个人资料表单状态
  const [profileName, setProfileName] = useState(profile.name);
  const [profileSex, setProfileSex] = useState<Sex>(profile.sex);
  const [profileHeight, setProfileHeight] = useState(String(profile.heightCm));
  const [profileGoal, setProfileGoal] = useState(profile.goalType);
  const [profileBodyFat, setProfileBodyFat] = useState(String(profile.targetBodyFatPercent ?? 15));

  // 伤情表单状态
  const [injuryActive, setInjuryActive] = useState(injury.active);
  const [injuryPart, setInjuryPart] = useState(injury.bodyPart);
  const [injuryType, setInjuryType] = useState(injury.injuryType);
  const [injuryBearing, setInjuryBearing] = useState(injury.weightBearingLevel);
  const [injuryForefoot, setInjuryForefoot] = useState(injury.forefootRestriction);
  const [injuryNotes, setInjuryNotes] = useState(injury.notes ?? "");

  useEffect(() => {
    if (settings?.profile) {
      setProfileName(settings.profile.name);
      setProfileSex(settings.profile.sex);
      setProfileHeight(String(settings.profile.heightCm));
      setProfileGoal(settings.profile.goalType);
      setProfileBodyFat(String(settings.profile.targetBodyFatPercent ?? 15));
    }
  }, [settings?.profile]);

  useEffect(() => {
    if (settings?.injury) {
      setInjuryActive(settings.injury.active);
      setInjuryPart(settings.injury.bodyPart);
      setInjuryType(settings.injury.injuryType);
      setInjuryBearing(settings.injury.weightBearingLevel);
      setInjuryForefoot(settings.injury.forefootRestriction);
      setInjuryNotes(settings.injury.notes ?? "");
    }
  }, [settings?.injury]);

  const onSaveProfile = async () => {
    const updated = {
      ...profile,
      name: profileName.trim() || "健身者",
      sex: profileSex,
      heightCm: parseFloat(profileHeight) || 175,
      goalType: profileGoal,
      targetBodyFatPercent: parseFloat(profileBodyFat) || 15,
      updatedAt: new Date().toISOString(),
    };
    await saveProfile(updated);
    setShowEditProfile(false);
  };

  const onSaveInjury = async () => {
    const updated = {
      ...injury,
      active: injuryActive,
      bodyPart: injuryPart.trim() || "none",
      injuryType: injuryType.trim() || "none",
      weightBearingLevel: injuryBearing,
      forefootRestriction: injuryForefoot,
      notes: (injuryNotes ?? "").trim(),
      updatedAt: new Date().toISOString(),
    };
    await saveInjury(updated);
    setShowEditInjury(false);
  };

  const goalName =
    profile.goalType === "muscle_gain_priority"
      ? "增肌优先"
      : profile.goalType === "fat_loss_priority"
        ? "减脂优先"
        : profile.goalType === "maintain"
          ? "维持塑形"
          : profile.goalType === "strength"
            ? "力量进阶"
            : "习惯养成";

  const bearingLabel =
    injury.weightBearingLevel === "full_weight_bearing"
      ? "完全承重（正常）"
      : injury.weightBearingLevel === "weight_bearing_as_tolerated"
        ? "耐受承重"
        : injury.weightBearingLevel === "partial_weight_bearing"
          ? "部分承重"
          : injury.weightBearingLevel === "touch_down_only"
            ? "仅脚尖轻触"
            : "完全不承重";

  const NAV_ITEMS = [
    {
      to: "/settings/training",
      title: "训练设置",
      desc: "序列分化 · 偏好 · 固定提醒",
      icon: (
        <svg viewBox="0 0 24 24" className="w-5 h-5 text-tone-accent" fill="none" stroke="currentColor" strokeWidth={2}>
          <path strokeLinecap="round" strokeLinejoin="round" d="M12 6V4m0 2a2 2 0 100 4m0-4a2 2 0 110 4m-6 8a2 2 0 100-4m0 4a2 2 0 110-4m0 4v2m0-6V4m6 6v10m6-2a2 2 0 100-4m0 4a2 2 0 110-4m0 4v2m0-6V4" />
        </svg>
      ),
    },
    {
      to: "/library",
      title: "动作库",
      desc: "动作检索 · 器械部位二级筛选",
      icon: (
        <svg viewBox="0 0 24 24" className="w-5 h-5 text-tone-accent" fill="none" stroke="currentColor" strokeWidth={2}>
          <path strokeLinecap="round" strokeLinejoin="round" d="M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10" />
        </svg>
      ),
    },
    {
      to: "/settings/account",
      title: "账户与同步",
      desc: "本地模式 · 出站队列 · 冲突留档",
      icon: (
        <svg viewBox="0 0 24 24" className="w-5 h-5 text-tone-accent" fill="none" stroke="currentColor" strokeWidth={2}>
          <path strokeLinecap="round" strokeLinejoin="round" d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12" />
        </svg>
      ),
    },
    {
      to: "/settings/export",
      title: "数据导出",
      desc: "JSON 完整备份 · CSV 记录明细",
      icon: (
        <svg viewBox="0 0 24 24" className="w-5 h-5 text-tone-accent" fill="none" stroke="currentColor" strokeWidth={2}>
          <path strokeLinecap="round" strokeLinejoin="round" d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
        </svg>
      ),
    },
    {
      to: "/settings/about",
      title: "关于与声明",
      desc: "PWA 安装 · 平台能力 · 医疗边界",
      icon: (
        <svg viewBox="0 0 24 24" className="w-5 h-5 text-tone-accent" fill="none" stroke="currentColor" strokeWidth={2}>
          <path strokeLinecap="round" strokeLinejoin="round" d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
        </svg>
      ),
    },
  ];

  return (
    <div className="space-y-4 pb-6">
      <header className="py-2">
        <h1 className="text-[28px] font-extrabold text-tone-ink tracking-tight">我的</h1>
        <div className="text-xs font-semibold text-tone-soft mt-0.5">个人资料 · 伤情限制 · 设置</div>
      </header>

      {/* 个人资料卡片 */}
      <SectionTitle>个人资料</SectionTitle>
      <Card className="p-4 border border-tone-border shadow-sm">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-13 h-13 rounded-2xl bg-tone-accent/15 flex items-center justify-center text-tone-accent text-xl font-bold shrink-0">
              {profile.name.slice(0, 1)}
            </div>
            <div>
              <div className="text-[16px] font-bold text-tone-ink">{profile.name}</div>
              <div className="text-[12px] text-tone-soft mt-0.5">
                {profile.sex === "male" ? "男" : "女"} · {profile.heightCm}cm · 目标体脂 {profile.targetBodyFatPercent ?? 15}%
              </div>
              <div className="text-[12px] text-tone-accent font-semibold mt-0.5">目标：{goalName}</div>
            </div>
          </div>
          <Button variant="secondary" className="text-xs px-3 min-h-[32px]" onClick={() => setShowEditProfile(true)}>
            编辑资料
          </Button>
        </div>
      </Card>

      {/* 伤情限制卡片（涉及医疗锁安全，首页常驻） */}
      <SectionTitle hint="动态计算可用性与医疗硬锁">伤情限制与防护</SectionTitle>
      <Card className="p-4 border border-tone-border shadow-sm">
        <div className="flex items-start justify-between">
          <div className="space-y-1.5 flex-1 pr-2">
            <div className="flex items-center gap-2 flex-wrap">
              <RiskTag tone={injury.active ? "danger" : "good"}>
                {injury.active ? `${injury.bodyPart} ${injury.injuryType}` : "无伤病限制"}
              </RiskTag>
              {injury.active ? <RiskTag tone="warn">{bearingLabel}</RiskTag> : null}
            </div>
            <p className="text-[12px] text-tone-soft leading-relaxed">
              {injury.active
                ? `${injury.notes || "前足受限；下肢复合动作为医疗锁锁定；阶段与解锁须按专业医疗许可。"}`
                : "当前未启用伤情限制，动作库全开放。"}
            </p>
          </div>
          <Button variant="secondary" className="text-xs px-3 min-h-[32px] shrink-0" onClick={() => setShowEditInjury(true)}>
            修改伤情
          </Button>
        </div>
      </Card>

      {/* iOS 设置风格分组导航列表 */}
      <SectionTitle>功能与设置</SectionTitle>
      <Card className="p-1 border border-tone-border shadow-sm divide-y divide-tone-border overflow-hidden">
        {NAV_ITEMS.map((item) => (
          <div
            key={item.to}
            onClick={() => nav(item.to)}
            className="flex items-center justify-between p-3.5 cursor-pointer hover:bg-black/[0.02] dark:hover:bg-white/[0.03] active:bg-black/[0.04] transition-all"
          >
            <div className="flex items-center gap-3.5">
              <div className="w-9 h-9 rounded-xl bg-black/5 dark:bg-white/10 flex items-center justify-center shrink-0">
                {item.icon}
              </div>
              <div>
                <div className="text-[14px] font-bold text-tone-ink">{item.title}</div>
                <div className="text-[11px] text-tone-soft mt-0.5">{item.desc}</div>
              </div>
            </div>
            <span className="text-tone-soft text-base font-semibold pr-1">›</span>
          </div>
        ))}
      </Card>

      {/* 个人资料编辑抽屉 */}
      <Sheet open={showEditProfile} onClose={() => setShowEditProfile(false)} title="编辑个人资料">
        <div className="p-4 space-y-4">
          <div>
            <label className="text-xs font-semibold text-tone-soft mb-1 block">昵称 / 姓名</label>
            <input
              type="text"
              value={profileName}
              onChange={(e) => setProfileName(e.target.value)}
              className="w-full min-h-[44px] px-4 rounded-xl bg-black/5 dark:bg-white/10 text-tone-ink font-semibold text-sm"
              placeholder="极练"
            />
          </div>

          <div>
            <label className="text-xs font-semibold text-tone-soft mb-1 block">性别</label>
            <Segmented
              value={profileSex}
              onChange={(v) => setProfileSex(v as Sex)}
              options={[
                { value: "male", label: "男" },
                { value: "female", label: "女" },
              ]}
            />
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-xs font-semibold text-tone-soft mb-1 block">身高 (cm)</label>
              <input
                type="number"
                value={profileHeight}
                onChange={(e) => setProfileHeight(e.target.value)}
                className="w-full min-h-[44px] px-4 rounded-xl bg-black/5 dark:bg-white/10 text-tone-ink font-semibold text-sm"
              />
            </div>
            <div>
              <label className="text-xs font-semibold text-tone-soft mb-1 block">目标体脂率 (%)</label>
              <input
                type="number"
                value={profileBodyFat}
                onChange={(e) => setProfileBodyFat(e.target.value)}
                className="w-full min-h-[44px] px-4 rounded-xl bg-black/5 dark:bg-white/10 text-tone-ink font-semibold text-sm"
              />
            </div>
          </div>

          <div>
            <label className="text-xs font-semibold text-tone-soft mb-1 block">健身主目标</label>
            <Segmented
              value={profileGoal}
              onChange={(v) => setProfileGoal(v as UserProfile["goalType"])}
              options={[
                { value: "muscle_gain_priority", label: "增肌优先" },
                { value: "fat_loss_priority", label: "减脂优先" },
                { value: "maintain", label: "维持塑形" },
                { value: "strength", label: "力量进阶" },
              ]}
            />
          </div>

          <Button className="w-full h-[48px] font-bold text-base mt-2" onClick={() => void onSaveProfile()}>
            保存个人资料
          </Button>
        </div>
      </Sheet>

      {/* 伤情编辑抽屉 */}
      <Sheet open={showEditInjury} onClose={() => setShowEditInjury(false)} title="修改伤情与运动限制">
        <div className="p-4 space-y-4">
          <Toggle
            checked={injuryActive}
            onChange={setInjuryActive}
            label="启用伤情保护（开启后受限动作自动加锁）"
          />

          {injuryActive ? (
            <>
              <div>
                <label className="text-xs font-semibold text-tone-soft mb-1 block">受伤部位</label>
                <input
                  type="text"
                  value={injuryPart}
                  onChange={(e) => setInjuryPart(e.target.value)}
                  placeholder="例如：右脚小脚趾、腰部、膝关节"
                  className="w-full min-h-[44px] px-4 rounded-xl bg-black/5 dark:bg-white/10 text-tone-ink font-semibold text-sm"
                />
              </div>

              <div>
                <label className="text-xs font-semibold text-tone-soft mb-1 block">承重等级</label>
                <Segmented
                  value={injuryBearing}
                  onChange={(v) => setInjuryBearing(v as InjuryConstraint["weightBearingLevel"])}
                  options={[
                    { value: "partial_weight_bearing", label: "部分承重" },
                    { value: "weight_bearing_as_tolerated", label: "耐受承重" },
                    { value: "full_weight_bearing", label: "完全承重" },
                  ]}
                />
              </div>

              <div>
                <label className="text-xs font-semibold text-tone-soft mb-1 block">前足/受力限制</label>
                <Segmented
                  value={injuryForefoot}
                  onChange={(v) => setInjuryForefoot(v as InjuryConstraint["forefootRestriction"])}
                  options={[
                    { value: "prohibited", label: "严格禁力" },
                    { value: "limited", label: "限制受力" },
                    { value: "unrestricted", label: "无限制" },
                  ]}
                />
              </div>

              <div>
                <label className="text-xs font-semibold text-tone-soft mb-1 block">医嘱与备注说明</label>
                <textarea
                  value={injuryNotes}
                  onChange={(e) => setInjuryNotes(e.target.value)}
                  rows={3}
                  className="w-full p-3 rounded-xl bg-black/5 dark:bg-white/10 text-tone-ink text-xs font-medium"
                  placeholder="可记录医生嘱咐、当前恢复周数等"
                />
              </div>
            </>
          ) : (
            <p className="text-xs text-tone-soft leading-relaxed p-3 bg-black/5 dark:bg-white/5 rounded-xl">
              关闭伤情保护后，系统将视为完全健康状态，所有杠铃深蹲、硬拉等复合动作将解除医疗锁。
            </p>
          )}

          <Button className="w-full h-[48px] font-bold text-base mt-2" onClick={() => void onSaveInjury()}>
            保存伤情设置
          </Button>
        </div>
      </Sheet>
    </div>
  );
}
